// ════════════════════════════════════════════════════════════════════════
//  IDIOMAS — inglés (por defecto) y español
//
//  Uso en un componente:
//      const t = useT();
//      <button>{t('profile.save')}</button>
//      t('saved.count', { n: 3, max: 10 })   →  "3 of 10 audios"
//
//  Fuera de React (módulos de audio, api.js) se usa `t` importado tal cual: lee
//  el idioma del momento. OJO: si un texto se calcula al IMPORTAR el módulo
//  (una constante de arriba del archivo), se queda congelado en el idioma de
//  ese instante. Esas constantes guardan la CLAVE y se traducen al pintar.
//
//  Cambiar de idioma NO recarga ni remonta la app: el rack y el audio siguen
//  sonando. Solo se vuelven a pintar los componentes que usan useT().
//
//  Los textos viven en ./locales/<zona>.js, cada uno con { en: {...}, es: {...} }.
//  Si falta una clave en inglés se usa la española, y si falta en las dos se
//  enseña la clave: así un olvido se ve en pantalla en vez de dejar un hueco.
// ════════════════════════════════════════════════════════════════════════
import { useCallback, useSyncExternalStore } from 'react';
import LOCALES from './locales';

// Fuera de STORAGE_KEYS a propósito, como el tema: es del equipo, no de la cuenta.
const KEY = 'dragonLang';
export const LANGS = ['en', 'es'];
export const DEFAULT_LANG = 'en';

const DICTS = { en: {}, es: {} };
LOCALES.forEach((mod) => {
  LANGS.forEach((l) => Object.assign(DICTS[l], mod[l] || {}));
});

function leer() {
  try {
    const v = localStorage.getItem(KEY);
    return LANGS.includes(v) ? v : DEFAULT_LANG;
  } catch (_) {
    return DEFAULT_LANG;
  }
}

let current = leer();
const listeners = new Set();

export const getLang = () => current;

export function setLang(lang) {
  if (!LANGS.includes(lang) || lang === current) return;
  current = lang;
  try { localStorage.setItem(KEY, lang); } catch (_) { /* modo privado: vale solo esta visita */ }
  document.documentElement.lang = lang;
  listeners.forEach((fn) => fn());
}

export function initLang() {
  document.documentElement.lang = current;
}

const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };

/** Traduce una clave. `vars` rellena los {huecos} del texto. */
export function t(key, vars) {
  let s = DICTS[current][key];
  if (s === undefined) s = DICTS.es[key];
  if (s === undefined) return key;
  if (typeof s === 'function') return s(vars || {});
  if (!vars) return s;
  return s.replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined ? String(vars[k]) : m));
}

/** ¿Existe la clave? Para textos opcionales. */
export const hasKey = (key) => DICTS.es[key] !== undefined || DICTS.en[key] !== undefined;

export function useLang() {
  return useSyncExternalStore(subscribe, getLang, getLang);
}

/** `t` atado al idioma actual: su identidad cambia con el idioma, así que
 *  sirve también como dependencia de useMemo/useCallback. */
export function useT() {
  const lang = useLang();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useCallback((key, vars) => t(key, vars), [lang]);
}

/** Locale para fechas y números (toLocaleDateString, Intl...). */
export const dateLocale = () => (current === 'es' ? 'es-CO' : 'en-US');
